/**
 * GroundTruth review — what the Additional Instructions and the source's
 * "Additional Notes from AM" (IO PDF or CTS workbook) did to this plan, shown
 * before approval.
 *
 * Reads the provenance the gt-api worker writes (gt_io_instructions.py):
 * `ctsReview.instruction_enrichment` — the review envelope's copy, whose
 * blocking-issue statuses are recomputed on every re-validation — falling back
 * to `ctsReview.result._instruction_enrichment`, the copy that travels inside
 * the config itself and so survives grid edits that skip re-validation.
 *
 * Everything the acceptance criteria ask the reviewer to see has its own
 * section: applied instructions with source and scope, the fields and rows
 * they changed with previous → resulting values, conflicts and overridden
 * values (winner, loser, both sources), unsupported / unapplied instructions,
 * ambiguous demographics, unmatched or ambiguous audiences with candidates, and
 * explicit budget and flight-date changes. Blocking findings also sit in
 * ctsReview.errors, which is what disables Approve & launch — this panel
 * explains them, it does not gate anything itself.
 *
 * Instructions are named the way a reviewer counts them — "Instruction 2",
 * "AM note 1" — never by the worker's internal ids (gtInstructionLabels.js).
 * Each instruction is listed once: a warning about one already shown under
 * Applied or Not applied sits on that row (or, when it only restates why the
 * row was not applied, is not repeated), so Other warnings holds only what
 * belongs to no listed instruction.
 */

import { useEffect, useState } from 'react';
import { assignmentOutcomeLabel } from './gtAssignmentOutcomes';
import {
  humanizeInstructionRefs, instructionLabel, withoutOwnLabel, writesInternalIds,
} from './gtInstructionLabels';

// The badge for a value no instruction set (the IO, a default), or for a
// finding that belongs to no single instruction.
const SOURCE_SHORT = {
  system_rule: 'Standard rule',
  additional_instructions: 'Your instructions',
  am_notes: 'AM notes',
  insertion_order: 'IO',
  default: 'Default',
};

const SOURCE_TITLE = {
  system_rule: 'Standard rule',
  additional_instructions: 'Additional Instructions',
  am_notes: 'Additional Notes from AM',
  insertion_order: 'Insertion order',
  default: 'Deterministic default',
};

// Warnings that only restate why their operation was not applied — the Not
// applied row already shows that status and reason.
const RESTATES_NOT_APPLIED = new Set(['unsupported_instruction', 'unsupported_field', 'protected_field']);

const isApplied = (o) => o.status === 'applied' || o.status === 'partially_applied';

const STATUS_LABEL = {
  applied: 'Applied',
  partially_applied: 'Partly applied',
  overridden: 'Overridden',
  blocked: 'Blocked',
  rejected: 'Rejected',
  unsupported: 'Not supported',
  not_applied: 'Not applied',
  pending: 'Pending',
};

const FIELD_LABEL = {
  io_budget: 'IO budget',
  budget: 'Budget',
  start_date: 'Start date',
  end_date: 'End date',
  package_name: 'Package name',
  demographic_targeting: 'Demographics',
  campaign_demographic_targeting: 'Campaign demographics',
  audiences: 'Audiences',
  drive_to_location_group: 'Drive-to location group',
  bid_type: 'Bid type',
  bid_price: 'Bid price',
  special_notes: 'Special notes',
  iab_category_resolved: 'IAB category',
};

const BUDGET_DATE_FIELDS = new Set(['budget', 'io_budget', 'start_date', 'end_date']);

export const fieldLabel = (f) => FIELD_LABEL[f] || String(f || '').replace(/_/g, ' ');

/** A config value as a reviewer reads it. */
export function formatValue(value, field) {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) {
    if (!value.length) return '—';
    return value.map((a) => (a && typeof a === 'object'
      ? `${a.name || 'Audience'}${String(a.id) === '49124' ? ' (placeholder)' : ` #${a.id}`}`
      : String(a))).join(', ');
  }
  if (typeof value === 'number' && (field === 'budget' || field === 'io_budget' || field === 'bid_price')) {
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (['drive_to_location_group', 'location_group'].includes(field) && typeof value === 'object') return `${value.name} #${value.id}`;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

const rowLabel = (rows) => {
  if (!rows?.length) return 'Campaign';
  const pkgs = [...new Set(rows.map((r) => r?.package_number).filter(Boolean))];
  if (rows.length === 1) return rows[0].ad_group_name || `Row ${rows[0].index + 1}`;
  return `${rows.length} ad groups${pkgs.length ? ` (PKG ${pkgs.join(', ')})` : ''}`;
};

/**
 * Where a warning about a listed instruction goes: { op } — shown on that
 * operation's row; { drop: true } — it restates the reason a Not applied row
 * already shows; null — its instruction is listed nowhere, so it stays under
 * Other warnings.
 */
function placeWarning(warning, listed, opById) {
  if (!warning.instruction_id) return null;
  const own = listed.filter((o) => o.instruction_id === warning.instruction_id);
  if (!own.length) return null;
  // Only ever against the warning's OWN operation: a sibling's short reason
  // ("budget") found inside an unrelated warning says nothing about it.
  const restates = (o) => !isApplied(o) && (RESTATES_NOT_APPLIED.has(warning.kind)
    || Boolean(o.reason && String(warning.message || '').includes(o.reason)));
  const exact = warning.operation_id ? opById.get(warning.operation_id) : null;
  if (exact && own.includes(exact)) return restates(exact) ? { drop: true } : { op: exact };
  // Reports written before warnings named their operation: the restating kinds
  // always belong to a not-applied operation; anything else goes on the
  // operation that touched the warning's field, else the first applied one.
  if (RESTATES_NOT_APPLIED.has(warning.kind) && own.some((o) => !isApplied(o))) return { drop: true };
  const fields = warning.fields || [];
  const target = own.find((o) => fields.some((f) => (o.affected_fields || []).includes(f)))
    || own.find(isApplied) || own[0];
  return restates(target) ? { drop: true } : { op: target };
}

/**
 * Pure view model — everything the panel renders, derived once. Exported for
 * tests; returns null when there is nothing to show (no instructions ran).
 */
export function summarizeInstructionEnrichment(ctsReview) {
  const e = ctsReview?.instruction_enrichment || ctsReview?.result?._instruction_enrichment;
  if (!e || typeof e !== 'object') return null;
  if (e.status === 'no_instructions') return null;

  // A report written with the ids in its messages reads the same as a new one;
  // a newer report's text is shown exactly as written.
  const say = writesInternalIds(e) ? humanizeInstructionRefs : (text) => text;
  const operations = (Array.isArray(e.operations) ? e.operations : [])
    .map((o) => (o.reason ? { ...o, reason: say(o.reason) } : o));
  const issues = (Array.isArray(e.issues) ? e.issues : []).map((i) => ({ ...i, message: say(i.message) }));
  const changes = Array.isArray(e.changes) ? e.changes : [];
  // Packages an instruction created (duplicate_package). Their ad groups'
  // budgets are ordinary changes below; this is the "what now exists" view.
  const packagesAdded = (Array.isArray(e.structure_changes) ? e.structure_changes : [])
    .filter((c) => c.kind === 'package_duplicated');

  // Collapse one instruction's identical edit across many rows into one line.
  const grouped = new Map();
  for (const c of changes) {
    const key = JSON.stringify([c.instruction_id, c.entity, c.field, c.previous, c.result]);
    const g = grouped.get(key) || { ...c, rows: [] };
    if (c.row) g.rows.push(c.row);
    grouped.set(key, g);
  }
  const changeGroups = [...grouped.values()];

  const blocking = issues.filter((i) => i.severity === 'blocking');
  const isAudience = (i) => i.kind === 'audience_unresolved' || i.kind === 'audience_ambiguous';

  const applied = operations.filter(isApplied);
  const notApplied = operations.filter((o) => !isApplied(o) && o.status !== 'pending');
  const opById = new Map(operations.map((o) => [o.id, o]));
  const listed = [...applied, ...notApplied];
  const notesByOperation = {};
  const otherWarnings = [];
  for (const w of issues) {
    if (w.severity !== 'warning' || ['demographic_ambiguous', 'demographic_partial'].includes(w.kind)) continue;
    const place = placeWarning(w, listed, opById);
    if (!place) otherWarnings.push(w);
    else if (place.op) (notesByOperation[place.op.id] = notesByOperation[place.op.id] || []).push(w);
  }
  return {
    assignmentSummary: e.assignment_summary,
    cacheSnapshot: e.cache_snapshot,
    status: e.status,
    error: e.error ? say(e.error) : null,
    snapshot: e.instruction_snapshot || {},
    sources: e.sources || {},
    processedAt: e.processed_at || null,
    processorVersion: e.processor_version || null,
    packagesAdded,
    applied,
    notApplied,
    changeGroups: changeGroups.filter((c) => !BUDGET_DATE_FIELDS.has(c.field)),
    budgetDateChanges: changeGroups.filter((c) => BUDGET_DATE_FIELDS.has(c.field)),
    // Instruction-vs-instruction conflicts, plus every value an instruction
    // displaced from the IO or a default — the review names both.
    conflicts: [
      ...(Array.isArray(e.conflicts) ? e.conflicts : []),
      ...changeGroups.filter((c) => c.overridden).map((c) => ({
        field: c.field,
        entity: c.entity,
        rows: c.rows,
        winning: { value: c.result, source: c.source, instruction_id: c.instruction_id },
        overridden: c.overridden,
        reason: 'instruction overrides the plan value',
      })),
    ],
    openBlocking: blocking.filter((i) => i.status === 'open' || !i.status),
    // Open blockers other than audiences; unresolved audiences get their own
    // section so their candidates read next to the rows they affect.
    openOther: blocking.filter((i) => (i.status === 'open' || !i.status) && !isAudience(i)),
    audiences: blocking.filter((i) => (i.status === 'open' || !i.status) && isAudience(i)),
    resolvedBlocking: blocking.filter((i) => i.status === 'resolved' || i.status === 'acknowledged'),
    demographics: issues.filter((i) => i.kind === 'demographic_ambiguous' || i.kind === 'demographic_partial'),
    // Warnings on a listed instruction, keyed by the operation row they show on.
    notesByOperation,
    // Only warnings that belong to no instruction listed above.
    otherWarnings,
  };
}

/** "Instruction 2" / "AM note 1" for an instruction; the source otherwise. */
function InstructionBadge({ source, instructionId }) {
  return (
    <span className={`clw-instr-src ${source || ''}`} title={SOURCE_TITLE[source] || undefined}>
      {instructionLabel(instructionId) || SOURCE_SHORT[source] || source || '—'}
    </span>
  );
}

function Section({ title, count, children, tone, collapsible = false }) {
  // Collapsible sections start closed: detail the reviewer opens on demand.
  const [open, setOpen] = useState(!collapsible);
  if (!count) return null;
  return (
    <div className={`clw-instr-section ${tone || ''}`}>
      {collapsible ? (
        <button
          type="button"
          className="clw-instr-section-title clw-instr-section-toggle"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="chev" aria-hidden="true">{open ? '▾' : '▸'}</span>
          {title} <span className="n">{count}</span>
        </button>
      ) : (
        <div className="clw-instr-section-title">{title} <span className="n">{count}</span></div>
      )}
      {open && children}
    </div>
  );
}

/** A warning shown on its own instruction's row, minus the label the badge carries. */
function OperationNotes({ notes, instructionId }) {
  if (!notes?.length) return null;
  return notes.map((n) => (
    <div key={n.id} className="clw-instr-reason warn">{withoutOwnLabel(n.message, instructionId)}</div>
  ));
}

export function GtInstructionReviewPanel({ ctsReview, onAcknowledge = null, busy = false }) {
  const vm = summarizeInstructionEnrichment(ctsReview);
  const openCount = vm ? vm.openBlocking.length : 0;
  const blocked = openCount > 0;
  // Collapsed by default: the detail is debugging evidence most reviewers never
  // need, and the header already counts what was processed. It opens while
  // something here must be resolved before launch — the audience picks and
  // "Keep current values" live in the body — and resolving it does not snap the
  // panel shut under the reviewer. Hide / Show always win.
  const [open, setOpen] = useState(blocked);
  useEffect(() => {
    if (blocked) setOpen(true);
  }, [blocked]);
  const [showText, setShowText] = useState(false);
  if (!vm) return null;
  const typed = vm.snapshot.additional_instructions || '';
  const am = vm.snapshot.am_notes || '';

  return (
    <div className={`clw-instr-panel ${openCount ? 'blocked' : ''}`} data-testid="gt-instruction-panel">
      <div className="clw-instr-head">
        <div className="clw-instr-title">
          {vm.assignmentSummary ? 'Instructions processed for this plan' : 'Instructions applied to this plan'}
          <span className="clw-instr-sub">
            {vm.status === 'failed'
              ? 'Enrichment failed — nothing was applied'
              : `${vm.applied.length} ${vm.assignmentSummary ? 'processed' : 'applied'} · ${vm.changeGroups.length + vm.budgetDateChanges.length} change${vm.changeGroups.length + vm.budgetDateChanges.length === 1 ? '' : 's'}`
                + (vm.packagesAdded.length ? ` · ${vm.packagesAdded.length} package${vm.packagesAdded.length === 1 ? '' : 's'} added` : '')
                + ` · ${vm.conflicts.length} override${vm.conflicts.length === 1 ? '' : 's'}`
                + (openCount ? ` · ${openCount} must be resolved before launch` : '')}
          </span>
          {vm.assignmentSummary && <span className="clw-instr-sub">
            Assignment outcomes at processing: {Object.entries(vm.assignmentSummary)
              .map(([outcome, count]) => `${count} ${assignmentOutcomeLabel(outcome).toLowerCase()}`).join(' · ')}
          </span>}
        </div>
        <button type="button" className="clw-issues-toggle" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide ▲' : 'Show ▼'}
        </button>
      </div>

      {open && (
        <div className="clw-instr-body">
          {vm.sources?.am_notes?.present && (
            <div className="clw-instr-note">
              {vm.sources.am_notes.sheet
                ? <>AM notes were read from the CTS workbook ({vm.sources.am_notes.sheet}{vm.sources.am_notes.cell ? `, cell ${vm.sources.am_notes.cell}` : ''}).</>
                : <>AM notes were read from the PDF{vm.sources.am_notes.page ? ` (page ${vm.sources.am_notes.page})` : ''}.</>}
            </div>
          )}
          {vm.error && <div className="clw-instr-error">{vm.error}</div>}
          {vm.cacheSnapshot && <details className="clw-instr-reason">
            <summary>Account cache snapshot used for matching</summary>
            <pre>{JSON.stringify(vm.cacheSnapshot, null, 2)}</pre>
          </details>}
          {vm.assignmentSummary && <div className="clw-instr-note">
            Matching evidence is a processing snapshot. Review edits do not rerun matching;
            the Excel export lists current selections separately from historical evidence.
          </div>}

          <Section title="Must be resolved before launch" count={vm.openOther.length} tone="err">
            <IssueList issues={vm.openOther} onAcknowledge={onAcknowledge} busy={busy} />
          </Section>

          <Section title="Audiences not assigned — pick one per affected ad group before launch" count={vm.audiences.length} tone="err">
            <IssueList issues={vm.audiences} />
          </Section>

          <Section title="Resolved in review" count={vm.resolvedBlocking.length} tone="ok">
            <ul className="clw-instr-list">
              {vm.resolvedBlocking.map((i) => (
                <li key={i.id} className="resolved">
                  <InstructionBadge source={i.source} instructionId={i.instruction_id} />
                  <span>{withoutOwnLabel(i.message, i.instruction_id)}</span>
                  {i.status === 'acknowledged' && (
                    <div className="clw-instr-reason">Kept the plan&apos;s current values — acknowledged in review{i.acknowledged_at ? ` ${new Date(i.acknowledged_at).toLocaleString()}` : ''}.</div>
                  )}
                </li>
              ))}
            </ul>
          </Section>

          <Section title="Packages added" count={vm.packagesAdded.length} tone="warn">
            <ul className="clw-instr-list">
              {vm.packagesAdded.map((c) => (
                <li key={`${c.instruction_id}-${c.target_package}`}>
                  <InstructionBadge source={c.source} instructionId={c.instruction_id} />
                  <span>
                    PKG {c.target_package} — copy of PKG {c.source_package}
                    {c.package_name ? ` (${c.package_name})` : ''}, {c.adgroup_count} ad group{c.adgroup_count === 1 ? '' : 's'}
                  </span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="Budget and flight-date changes" count={vm.budgetDateChanges.length} tone="warn">
            <ChangeTable groups={vm.budgetDateChanges} />
          </Section>

          <Section title={vm.assignmentSummary ? 'Processed instructions' : 'Applied instructions'} count={vm.applied.length}>
            <ul className="clw-instr-list">
              {vm.applied.map((o) => (
                <li key={o.id}>
                  <InstructionBadge source={o.source} instructionId={o.instruction_id} />
                  <span className="clw-instr-text">“{o.original_text}”</span>
                  <span className={`clw-instr-status ${o.status}`}>
                    {o.assignment_outcome ? assignmentOutcomeLabel(o.assignment_outcome) : STATUS_LABEL[o.status] || o.status}
                  </span>
                  <span className="clw-instr-rows">
                    {o.affected_fields?.map(fieldLabel).join(', ')}
                    {o.affected_rows?.length ? ` · ${rowLabel(o.affected_rows)}` : ' · campaign'}
                  </span>
                  {o.reason && <div className="clw-instr-reason">{o.reason}</div>}
                  <TargetingEvidence outcomes={o.targeting_resolution} />
                  {o.assignment_outcomes?.length > 0 && <ul className="clw-instr-reason">
                    {o.assignment_outcomes.map((a) => <li key={`${a.row?.index}-${a.role}`}>
                      {rowLabel(a.row ? [a.row] : [])} · {fieldLabel(a.role)}: {assignmentOutcomeLabel(a.outcome)}
                      {a.placeholder_present ? ' · Placeholder present' : ' · No placeholder'}
                    </li>)}
                  </ul>}
                  <OperationNotes notes={vm.notesByOperation[o.id]} instructionId={o.instruction_id} />
                </li>
              ))}
            </ul>
          </Section>

          <Section title="Fields changed" count={vm.changeGroups.length} collapsible>
            <ChangeTable groups={vm.changeGroups} />
          </Section>

          <Section title="Conflicts and overridden values" count={vm.conflicts.length}>
            <table className="clw-instr-table">
              <thead><tr><th>Field</th><th>Rows</th><th>Winning value</th><th>Overridden value</th></tr></thead>
              <tbody>
                {vm.conflicts.map((c, n) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={n}>
                    <td>{fieldLabel(c.field)}</td>
                    <td>{rowLabel(c.rows || (c.row ? [c.row] : []))}</td>
                    <td>{formatValue(c.winning?.value, c.field)} <InstructionBadge source={c.winning?.source} instructionId={c.winning?.instruction_id} /></td>
                    <td className="old">{formatValue(c.overridden?.value, c.field)} <InstructionBadge source={c.overridden?.source} instructionId={c.overridden?.instruction_id} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Demographics not applied as written" count={vm.demographics.length} tone="warn">
            <ul className="clw-instr-list">
              {vm.demographics.map((i) => (
                <li key={i.id}>
                  <InstructionBadge source={i.source} instructionId={i.instruction_id} />
                  <span>{withoutOwnLabel(i.message, i.instruction_id)}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="Not applied" count={vm.notApplied.length} tone="warn">
            <ul className="clw-instr-list">
              {vm.notApplied.map((o) => (
                <li key={o.id}>
                  <InstructionBadge source={o.source} instructionId={o.instruction_id} />
                  <span className="clw-instr-text">“{o.original_text}”</span>
                  <span className={`clw-instr-status ${o.status}`}>{STATUS_LABEL[o.status] || o.status}</span>
                  {o.reason && <div className="clw-instr-reason">{o.reason}</div>}
                  <TargetingEvidence outcomes={o.targeting_resolution} />
                  <OperationNotes notes={vm.notesByOperation[o.id]} instructionId={o.instruction_id} />
                </li>
              ))}
            </ul>
          </Section>

          <Section title="Other warnings" count={vm.otherWarnings.length} tone="warn" collapsible>
            <ul className="clw-instr-list">
              {vm.otherWarnings.map((i) => (
                <li key={i.id}>
                  <InstructionBadge source={i.source} instructionId={i.instruction_id} />
                  <span>{withoutOwnLabel(i.message, i.instruction_id)}</span>
                </li>
              ))}
            </ul>
          </Section>

          {(typed || am) && (
            <div className="clw-instr-original">
              <button type="button" className="clw-instr-link" onClick={() => setShowText((v) => !v)}>
                {showText ? 'Hide original instruction text' : 'Show original instruction text'}
              </button>
              {showText && (
                <div className="clw-instr-original-body">
                  {typed && (<><div className="lbl">Additional Instructions</div><pre>{typed}</pre></>)}
                  {am && (<><div className="lbl">Additional Notes from AM</div><pre>{am}</pre></>)}
                </div>
              )}
            </div>
          )}
          {vm.processorVersion && (
            <div className="clw-instr-meta">
              {vm.processorVersion}{vm.processedAt ? ` · ${new Date(vm.processedAt).toLocaleString()}` : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TargetingEvidence({ outcomes }) {
  if (!Array.isArray(outcomes) || !outcomes.length) return null;
  return (
    <details className="clw-instr-reason">
      <summary>Targeting match evidence ({outcomes.length})</summary>
      <ul>
        {outcomes.map((o, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={index}>
            {rowLabel(o.row ? [o.row] : [])} · {fieldLabel(o.role)} · {o.query || 'No scoped description'}
            <div>
              {o.resolved ? `${o.resolved.name} (${o.resolved.type} #${o.resolved.id})`
                : `${o.placeholder_present === false ? 'Not assigned; no placeholder added'
                  : o.placeholder_present === true ? 'Creation fallback present' : 'Unresolved (historical report)'}: ${fieldLabel(o.reason || 'no safe match')}`}
              {o.resolved && o.resolved_present === false && ' · Match not present in final selections'}
              {o.score != null && ` · Match score ${o.score}/100; threshold ${o.threshold}; margin ${o.margin} (minimum ${o.required_margin})`}
            </div>
            {o.scorer && <div>Scorer: {o.scorer} · Cache: {o.pool_status
              || (o.pool_count != null ? `${o.pool_count} eligible records` : o.candidates?.[0]?.source || 'not recorded')}
              {o.description_source && ` · Description: ${fieldLabel(o.description_source)}`}
            </div>}
            {o.candidates?.length > 0 && !o.resolved && (
              <div>Candidates (not assigned): {o.candidates.map((c) => `${c.record.name} (${c.record.type} #${c.record.id}, score ${c.score})`).join('; ')}</div>
            )}
            {o.rejected_candidates?.length > 0 && <div>Rejected (not assigned): {o.rejected_candidates
              .map((c) => `${c.record.name} (#${c.record.id}): ${fieldLabel(c.reason)}`).join('; ')}</div>}
          </li>
        ))}
      </ul>
    </details>
  );
}

function IssueList({ issues, onAcknowledge = null, busy = false }) {
  return (
    <ul className="clw-instr-list">
      {issues.map((i) => (
        <li key={i.id}>
          <InstructionBadge source={i.source} instructionId={i.instruction_id} />
          <span>{withoutOwnLabel(i.message, i.instruction_id)}</span>
          {onAcknowledge && i.acknowledgeable && (
            <button
              type="button"
              className="clw-instr-ack"
              disabled={busy}
              onClick={() => onAcknowledge(i.id)}
              title="Do not apply this instruction; keep the plan's current values. Recorded in the plan's history."
            >
              Keep current values
            </button>
          )}
          {i.rows?.length > 0 && <span className="clw-instr-rows">{rowLabel(i.rows)}</span>}
          {i.candidates?.length > 0 && (
            <div className="clw-instr-cands">
              Candidates (not assigned):{' '}
              {i.candidates.map((c) => `${c.name} (${c.type} #${c.id}${c.match === 'fuzzy' ? ', similar' : ''})`).join('; ')}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

function ChangeTable({ groups }) {
  return (
    <table className="clw-instr-table">
      <thead><tr><th>Field</th><th>Rows</th><th>Previous</th><th>Result</th><th>Source</th></tr></thead>
      <tbody>
        {groups.map((c, n) => (
          // eslint-disable-next-line react/no-array-index-key
          <tr key={n}>
            <td>{fieldLabel(c.field)}</td>
            <td>{rowLabel(c.rows)}</td>
            <td className="old">{formatValue(c.previous, c.field)}</td>
            <td>{formatValue(c.result, c.field)}</td>
            <td><InstructionBadge source={c.source} instructionId={c.instruction_id} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default GtInstructionReviewPanel;
